import React from 'react'
import { ComponentConfig } from '@puckeditor/core'
import { getPuckFileUrl, UploadedFile } from '../../fields/FileUploadField'
import type { ThemeCustomFields } from '../../theme-config'
import { withStyleFields } from '../StyleWrapper'
import { withLayout } from '../Layout'
import {
  BackgroundStyleProps,
  BorderStyleProps,
  applyStyleProps,
} from '../../fields/StyleFields'
import * as styles from './FullScreenBanner.module.scss'

export type FullScreenBannerProps = {
  desktopImage: UploadedFile | string
  mobileImage: UploadedFile | string
  alt: string
  link: string
  background?: BackgroundStyleProps
  border?: BorderStyleProps
}

// withLayout adds padding/margin/size on a wrapper (BR-5406); style fields
// stay on the inner withStyleFields so border-radius applies to the images.
export const PuckFullScreenBanner = withLayout(
  withStyleFields<ComponentConfig<{
    props: FullScreenBannerProps
    fields: ThemeCustomFields
  }>>({
  label: 'Full Screen Banner',
  defaultProps: {
    desktopImage: '',
    mobileImage: '',
    alt: '',
    link: '',
  },
  fields: {
    desktopImage: { type: 'fileUpload', label: 'Desktop Image' },
    mobileImage: { type: 'fileUpload', label: 'Mobile Image' },
    alt: { type: 'text', label: 'Alt Text' },
    link: { type: 'text', label: 'Link URL (optional)' },
  },
  render: ({ desktopImage, mobileImage, alt, link, background, border }) => {
    const desktopUrl = getPuckFileUrl(desktopImage)
    const mobileUrl = getPuckFileUrl(mobileImage)
    const containerStyle = applyStyleProps({ background, border })

    if (!desktopUrl && !mobileUrl) {
      return <div className={styles.placeholder} style={containerStyle}>No banner image selected</div>
    }

    // Apply border-radius directly to <img> elements so they match the container's
    // rounded corners without needing overflow:hidden hacks.
    const imgStyle = border?.borderRadius ? { borderRadius: border.borderRadius } : undefined

    const images = (
      <>
        {desktopUrl && (
          <img
            src={desktopUrl}
            alt={alt}
            className={styles.desktopImage}
            style={imgStyle}
          />
        )}
        {mobileUrl ? (
          <img
            src={mobileUrl}
            alt={alt}
            className={styles.mobileImage}
            style={imgStyle}
          />
        ) : (
          desktopUrl && (
            <img
              src={desktopUrl}
              alt={alt}
              className={styles.mobileImage}
              style={imgStyle}
            />
          )
        )}
      </>
    )

    if (link) {
      return (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.wrapper}
          style={{ cursor: 'pointer', ...containerStyle }}
        >
          {images}
        </a>
      )
    }

    return <div className={styles.wrapper} style={containerStyle}>{images}</div>
    },
  }),
  { disableStyleFields: true },
)
